<template>
     <section class="w-full min-h-full"
                style="min-height: calc(100vh - 10rem); display: flex; align-items: center;">
                <div class="px-4 md:px-6 lg:px-8 xl:px-10">
                    <div class="grid gap-6 lg:grid-cols-[1fr_400px] lg:gap-12 xl:grid-cols-[1fr_600px]">
                        <div class="flex flex-col justify-center space-y-4">
                            <div class="space-y-2">
                                <h1 class="text-4xl sm:text-6xl xl:text-7xl font-bold tracking-tighter font-display preload-text">
                                    Gamify your growth on <br>
                                    <span class="platform-text" :class="platformAnimation">{{ currentPlatform }}.</span>
                                </h1>
                                <p class="max-w-[600px] text-gray-500 md:text-2xl">
                                    Level up your social media presence with daily missions, achievements, and friendly
                                    competition.
                                </p>
                            </div>
                            <div class="flex flex-col gap-2 min-[400px]:flex-row">
                                <router-link
                                    class="inline-flex h-10 items-center justify-center rounded-md bg-gray-900 px-8 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50"
                                    to="#pricing">
                                    Get Started
                                </router-link>
                                <router-link
                                    class="inline-flex h-10 items-center justify-center rounded-md border border-gray-200 bg-white px-8 text-sm font-medium shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50"
                                    to="#about">
                                    Learn More
                                </router-link>
                            </div>
                        </div>
                        <div>
                            <div v-for="mission in missions" :key="mission.id"
                                :class="{ dance: mission.progress === 100 }" class="mb-4 flex items-center">
                                <font-awesome-icon :icon="mission.icon" size="2x" class="text-black mr-4" />
                                <div class="flex-1">
                                    <div class="flex items-center mb-4">
                                        <h2 class="text-xl font-semibold">{{ mission.title }}</h2>
                                        <font-awesome-icon v-if="mission.progress === 100" icon="fa-solid fa-check"
                                            class="text-2xl text-green-400 ml-3 rounded-full" />
                                    </div>
                                    <div class="w-full bg-gray-200 rounded-full h-4">
                                        <div :style="{ width: mission.progress + '%' }"
                                            class="bg-black h-4 rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import confetti from 'canvas-confetti';

interface Mission {
    id: number;
    title: string;
    progress: number;
    icon: string;
}

const missions = ref<Mission[]>([
    { id: 1, title: 'Create 2 Posts', progress: 0, icon: 'fa-brands fa-x-twitter' },
    { id: 2, title: 'Upload 1 Video', progress: 0, icon: 'fa-brands fa-youtube' },
    { id: 3, title: 'Gain 20 Followers', progress: 0, icon: 'fa-brands fa-instagram' },
]);

const platforms = ['Reddit', 'X', 'YouTube', 'Instagram'];
const currentPlatform = ref(platforms[0]);
const platformAnimation = ref('');

function rotatePlatform() {
    let index = 0;
    setInterval(() => {
        platformAnimation.value = 'fly-to-top';
        setTimeout(() => {
            index = (index + 1) % platforms.length;
            currentPlatform.value = platforms[index];
            platformAnimation.value = 'come-from-bottom';
        }, 500); // Duration of the fly-to-top animation
    }, 2000); // Change platform every 2 seconds
}

function lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
}

onMounted(() => {
    rotatePlatform();
    missions.value.forEach((mission, index) => {
        setTimeout(() => {
            let startTime: number | null = null;
            const duration = 1000 * 1;

            function animate(timestamp: number) {
                if (!startTime) startTime = timestamp;
                const elapsed = timestamp - startTime;
                const t = Math.min(elapsed / duration, 1); // Ensure t is between 0 and 1

                mission.progress = lerp(0, 100, t);

                if (t < 1) {
                    requestAnimationFrame(animate);
                }
            }

            requestAnimationFrame(animate);
        }, index * 1000);
    });
});

watch(missions, (newMissions) => {
    if (newMissions.every(mission => mission.progress === 100)) {
        confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.7 },
        });
    }
}, { deep: true });
</script>

<style scoped>
@keyframes dance {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-10px);
    }

    50% {
        transform: translateX(10px);
    }

    75% {
        transform: translateX(-10px);
    }
}

.dance {
    animation: dance 0.5s ease-in-out;
}

@keyframes invisible {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

@keyframes visible {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

.fly-to-top {
    animation: invisible 0.5s forwards;
}

.come-from-bottom {
    animation: visible 0.5s forwards;
}

.font-display {
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
}

.preload-text {
    content-visibility: auto;
    contain-intrinsic-size: 0 100px;
}
</style>